import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  DatePicker,
  Dropdown,
  Empty,
  Input,
  Menu,
  Select,
  Table,
  message,
} from "antd";
import type { TableColumnsType, TablePaginationConfig } from "antd";
import type { Dayjs } from "dayjs";
import useGetDetailLogs, {
  type DetailLog,
  type LogFilterParams,
} from "../../hooks/getDetailLog/useGetDetailLog";
import { useProcessContext } from "../../context/ProcessContext";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ClearOutlined,
  DownOutlined,
  ArrowLeftOutlined,
  ReloadOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import ExportTableButton from "layouts/LayoutTableExport/LayoutTableExport";
import { getProcessDisplayName } from "../../utils/processLabels";
import TableSkeleton from "../../ui/table/TableSkeleton";
import { TABLE_PAGINATION_UX } from "../../ui/table/paginationDefaults";
import CopyableTruncatedCell from "../../components/CopyableTruncatedCell";
import LogStatusTag, {
  getLogStatusLabel,
} from "../../components/LogStatusTag/LogStatusTag";
import "./DetailLogPage.css";

const SEARCH_DEBOUNCE_MS = 400;
/** When searching, pull a wider page so client filter can match beyond the UI page size. */
const SEARCH_FETCH_SIZE = 500;

type DetailLogLocationState = {
  /** When opening a FAILED execution, preselect item status filter. */
  initialItemStatus?: string;
};

const renderCopyable = (
  value: string | null | undefined,
  copySuccessMessage: string
) => {
  if (value == null || value === "") return "—";
  return (
    <CopyableTruncatedCell
      value={String(value)}
      copySuccessMessage={copySuccessMessage}
      onlyWhenTruncated={false}
    />
  );
};

/**
 * Match Order VTEX / product ref against row.refid or request JSON.
 * ASSUMPTION: API `refId` filter is unreliable for order process; client filters as safety net.
 */
const matchesRefSearch = (row: DetailLog, query: string): boolean => {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const ref = String(row.refid ?? "").toLowerCase();
  if (ref.includes(q)) return true;
  const request = String(row.request ?? "").toLowerCase();
  return request.includes(q);
};

const LOG_ITEM_STATUS_OPTIONS = [
  { value: "", label: "Todos (estado)" },
  { value: "OK", label: "Exitoso" },
  { value: "FAILED", label: "Fallido" },
];

const DetailLogPage: React.FC = () => {
  const { selectedProcessType, logId } = useProcessContext();
  const location = useLocation();
  const locationState = (location.state ?? null) as DetailLogLocationState | null;
  const {
    detailLog,
    loadingDetailLog,
    errorDetailLog,
    totalElements,
    fetchDetailLog,
  } = useGetDetailLogs();
  const [loading, setLoading] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [debouncedRefId, setDebouncedRefId] = useState("");
  // ASSUMPTION: Prefer FAILED when navigated from a failed execution row.
  const [itemStatusFilter, setItemStatusFilter] = useState(
    () => locationState?.initialItemStatus ?? ""
  );
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null]>([
    null,
    null,
  ]);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const navigate = useNavigate();

  const isOrderProcess = useMemo(
    () =>
      (detailLog?.[0]?.process ?? "").toLowerCase() === "order" ||
      Boolean(detailLog?.some((row) => row.process.toLowerCase() === "order")),
    [detailLog]
  );

  const processLabel = useMemo(
    () => getProcessDisplayName(detailLog?.[0]?.process),
    [detailLog]
  );

  const allColumns = useMemo(() => {
    if (isOrderProcess) {
      return [
        {
          title: "Order VTEX",
          dataIndex: "refid",
          key: "refid",
          width: 150,
          render: (value: string) =>
            renderCopyable(value, "Order VTEX copiado"),
        },
        {
          title: "Order ERP / Doc",
          dataIndex: "name",
          key: "name",
          width: 140,
          render: (value: string) =>
            renderCopyable(value, "Documento copiado"),
        },
        {
          title: "Solicitud",
          dataIndex: "request",
          key: "request",
          width: 160,
          render: (value: string) =>
            renderCopyable(value, "Solicitud copiada"),
        },
        {
          title: "Mensaje",
          dataIndex: "message",
          key: "message",
          width: 180,
          render: (value: string) => renderCopyable(value, "Mensaje copiado"),
        },
        {
          title: "HTTP",
          dataIndex: "response",
          key: "response",
          width: 88,
          render: (value: string) => renderCopyable(value, "HTTP copiado"),
        },
        {
          title: "Estado ejecución",
          dataIndex: "status",
          key: "status",
          width: 130,
          render: (value: string) => <LogStatusTag status={value} />,
        },
        {
          title: "Estado ítem",
          dataIndex: "itemStatus",
          key: "itemStatus",
          width: 120,
          render: (value: string) => <LogStatusTag status={value} />,
        },
      ];
    }

    return [
      {
        title: "Referencia",
        dataIndex: "refid",
        key: "refid",
        width: 140,
        render: (value: string) =>
          renderCopyable(value, "Referencia copiada"),
      },
      {
        title: "SKU",
        dataIndex: "name",
        key: "name",
        width: 140,
        render: (value: string) => renderCopyable(value, "SKU copiado"),
      },
      {
        title: "Solicitud",
        dataIndex: "request",
        key: "request",
        width: 160,
        render: (value: string) => renderCopyable(value, "Solicitud copiada"),
      },
      {
        title: "Mensaje",
        dataIndex: "message",
        key: "message",
        width: 180,
        render: (value: string) => renderCopyable(value, "Mensaje copiado"),
      },
      {
        title: "HTTP",
        dataIndex: "response",
        key: "response",
        width: 88,
        render: (value: string) => renderCopyable(value, "HTTP copiado"),
      },
      {
        title: "Estado ítem",
        dataIndex: "itemStatus",
        key: "itemStatus",
        width: 120,
        render: (value: string) => <LogStatusTag status={value} />,
      },
    ];
  }, [isOrderProcess]);

  const [selectedColumns, setSelectedColumns] = useState<string[]>([]);

  useEffect(() => {
    setSelectedColumns(allColumns.map((col) => col.key));
  }, [allColumns]);

  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const activeFilters = useMemo<LogFilterParams>(() => {
    const fromDate = dateRange[0]
      ? dateRange[0].format("YYYY-MM-DD")
      : undefined;
    const toDate = dateRange[1]
      ? dateRange[1].format("YYYY-MM-DD")
      : undefined;
    return {
      refId: debouncedRefId.trim() || undefined,
      // ASSUMPTION: API still requires onlyFailed; UI filter is item status only.
      onlyFailed: false,
      itemStatus: itemStatusFilter || undefined,
      fromDate,
      toDate,
    };
  }, [debouncedRefId, itemStatusFilter, dateRange]);

  const searchQuery = debouncedRefId.trim();
  const isRefSearching = searchQuery.length > 0;
  const hasActiveFilters = Boolean(
    searchQuery || itemStatusFilter || dateRange[0] || dateRange[1]
  );

  const isInvalidDateRange = Boolean(
    activeFilters.fromDate &&
      activeFilters.toDate &&
      activeFilters.fromDate > activeFilters.toDate
  );

  const columns: TableColumnsType<DetailLog> = allColumns.filter((column) =>
    selectedColumns.includes(column.key)
  );

  const loadPage = (
    page: number,
    size: number,
    filters: LogFilterParams = activeFilters
  ) => {
    if (!logId || selectedProcessType === null || selectedProcessType <= 0) {
      return Promise.resolve();
    }
    if (
      filters.fromDate &&
      filters.toDate &&
      filters.fromDate > filters.toDate
    ) {
      message.error(
        "Rango de fechas inválido: la fecha inicio no puede ser mayor que la fin."
      );
      return Promise.resolve();
    }
    const searching = Boolean(filters.refId?.trim());
    // ASSUMPTION: When searching, fetch a wider window then filter client-side.
    const requestPage = searching ? 1 : page;
    const requestSize = searching ? SEARCH_FETCH_SIZE : size;
    return fetchDetailLog(
      String(logId),
      selectedProcessType,
      requestPage,
      requestSize,
      filters
    );
  };

  const onReload = () => {
    if (!logId || selectedProcessType === null || selectedProcessType <= 0) {
      return;
    }
    setLoading(true);
    loadPage(pagination.current || 1, pagination.pageSize || 10).finally(() =>
      setLoading(false)
    );
  };

  const handleClearFilters = () => {
    setSearchValue("");
    setDebouncedRefId("");
    setItemStatusFilter("");
    setDateRange([null, null]);
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleColumnSelection = (columnKey: string, checked: boolean) => {
    setSelectedColumns((prev) =>
      checked ? [...prev, columnKey] : prev.filter((key) => key !== columnKey)
    );
  };

  const handleTableChange = (next: TablePaginationConfig) => {
    setPagination(next);
    // ASSUMPTION: refId search paginates client-side over the fetched window.
    if (debouncedRefId.trim()) return;
    void loadPage(next.current || 1, next.pageSize || 10);
  };

  const columnSelectionMenu = (
    <Menu>
      {allColumns.map((col) => (
        <Menu.Item key={col.key}>
          <Checkbox
            checked={selectedColumns.includes(col.key)}
            onChange={(e) => handleColumnSelection(col.key, e.target.checked)}
          >
            {col.title}
          </Checkbox>
        </Menu.Item>
      ))}
    </Menu>
  );

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedRefId(searchValue.trim());
      setPagination((prev) =>
        prev.current === 1 ? prev : { ...prev, current: 1 }
      );
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchValue]);

  // When searching, keep a single wide fetch; page changes are local.
  const fetchPage = isRefSearching ? 1 : pagination.current;
  const fetchPageSize = isRefSearching ? SEARCH_FETCH_SIZE : pagination.pageSize;

  useEffect(() => {
    if (!logId || !selectedProcessType) return;
    void loadPage(fetchPage || 1, fetchPageSize || 10);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- filters/pagination drive refetch
  }, [logId, selectedProcessType, fetchDetailLog, fetchPage, fetchPageSize, activeFilters]);

  const filteredDetailLog = useMemo(() => {
    if (!detailLog) return [];
    if (!isRefSearching) return detailLog;
    return detailLog.filter((row) => matchesRefSearch(row, searchQuery));
  }, [detailLog, isRefSearching, searchQuery]);

  const currentPage = pagination.current || 1;
  const currentPageSize = pagination.pageSize || 10;

  const pagedDetailLog = useMemo(() => {
    if (!isRefSearching) return filteredDetailLog;
    const start = (currentPage - 1) * currentPageSize;
    return filteredDetailLog.slice(start, start + currentPageSize);
  }, [filteredDetailLog, isRefSearching, currentPage, currentPageSize]);

  useEffect(() => {
    const nextTotal = isRefSearching
      ? filteredDetailLog.length
      : totalElements;
    setPagination((prev) =>
      prev.total === nextTotal ? prev : { ...prev, total: nextTotal }
    );
  }, [totalElements, isRefSearching, filteredDetailLog.length]);

  const isBusy = loading || loadingDetailLog;
  const searchLabel = isOrderProcess ? "Order VTEX" : "Referencia";
  const searchPlaceholder = isOrderProcess
    ? "Ej. 1651571107963-01"
    : "Ej. STN-CBEP653";
  const searchHint = isOrderProcess
    ? "Busca por Order VTEX (refId) dentro de esta ejecución."
    : "Busca por referencia de producto (refId) dentro de esta ejecución.";

  const exportData = useMemo(
    () =>
      filteredDetailLog.map((row) => ({
        ...row,
        status: getLogStatusLabel(row.status),
        itemStatus: getLogStatusLabel(row.itemStatus),
      })),
    [filteredDetailLog]
  );

  const resultCount = pagination.total ?? 0;

  return (
    <div className="page__content detail-log-page">
      <div className="detail-log-page__header">
        <p className="page__section-label">Detalle Log: {processLabel}</p>
        <p className="detail-log-page__meta">
          {logId != null ? (
            <>
              Ejecución <strong>#{logId}</strong>
              {resultCount > 0 || !loadingDetailLog ? (
                <>
                  {" · "}
                  <strong>{resultCount.toLocaleString("es-CO")}</strong>{" "}
                  {resultCount === 1 ? "registro" : "registros"}
                </>
              ) : null}
            </>
          ) : (
            "Sin ejecución seleccionada"
          )}
        </p>
      </div>

      <div className="detail-log-page__toolbar">
        <div className="detail-log-page__toolbar-group detail-log-page__toolbar-group--actions">
          <span className="detail-log-page__toolbar-label">Acciones</span>
          <div className="detail-log-page__toolbar-control-row">
            <Button
              type="primary"
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate("/logs")}
              disabled={isBusy}
            >
              Volver
            </Button>
            <Button
              type="primary"
              icon={<ReloadOutlined />}
              onClick={onReload}
              loading={isBusy}
            >
              {isBusy ? "Actualizando" : "Actualizar"}
            </Button>
            <Button
              icon={<ClearOutlined />}
              onClick={handleClearFilters}
              disabled={isBusy || !hasActiveFilters}
            >
              Limpiar filtros
            </Button>
            <ExportTableButton
              data={exportData}
              columns={columns}
              disabled={isBusy || exportData.length === 0}
              variant="default"
            />
            <Dropdown
              overlay={columnSelectionMenu}
              trigger={["click"]}
              disabled={isBusy}
            >
              <Button
                className="detail-log-page__toolbar-columns-btn"
                disabled={isBusy}
              >
                Columnas <DownOutlined />
              </Button>
            </Dropdown>
          </div>
        </div>

        <div
          className="detail-log-page__toolbar-separator"
          aria-hidden="true"
        />

        <div className="detail-log-page__toolbar-group detail-log-page__toolbar-group--filters">
          <span className="detail-log-page__toolbar-label detail-log-page__toolbar-label--section">
            Filtros
          </span>
          <div className="detail-log-page__toolbar-filters-grid">
            <div className="detail-log-page__toolbar-field detail-log-page__toolbar-field--search">
              <label
                htmlFor="detail-log-search"
                className="detail-log-page__toolbar-label"
              >
                {searchLabel}
              </label>
              <Input
                id="detail-log-search"
                className="detail-log-page__toolbar-search"
                placeholder={searchPlaceholder}
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                allowClear
                spellCheck={false}
                disabled={isBusy}
                suffix={
                  <SearchOutlined
                    className="detail-log-page__toolbar-search-icon"
                    aria-hidden="true"
                  />
                }
              />
              <p className="detail-log-page__field-hint">{searchHint}</p>
            </div>

            <div className="detail-log-page__toolbar-field detail-log-page__toolbar-field--dates">
              <span className="detail-log-page__toolbar-label">Fechas</span>
              <DatePicker.RangePicker
                className="detail-log-page__date-range"
                value={dateRange}
                placeholder={["Desde", "Hasta"]}
                format="YYYY-MM-DD"
                disabled={isBusy}
                status={isInvalidDateRange ? "error" : undefined}
                onChange={(values) => {
                  setDateRange(
                    values
                      ? [values[0] ?? null, values[1] ?? null]
                      : [null, null]
                  );
                  setPagination((prev) =>
                    prev.current === 1 ? prev : { ...prev, current: 1 }
                  );
                }}
              />
            </div>

            <div className="detail-log-page__toolbar-field detail-log-page__toolbar-field--status">
              <span className="detail-log-page__toolbar-label">Estado</span>
              <Select
                className="detail-log-page__status-select"
                value={itemStatusFilter}
                options={LOG_ITEM_STATUS_OPTIONS}
                disabled={isBusy}
                onChange={(value: string) => {
                  setItemStatusFilter(value);
                  setPagination((prev) =>
                    prev.current === 1 ? prev : { ...prev, current: 1 }
                  );
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {errorDetailLog && (
        <Alert
          type="error"
          showIcon
          closable
          className="page__alert detail-log-page__alert"
          message={errorDetailLog}
        />
      )}

      {loadingDetailLog && (!detailLog || detailLog.length === 0) ? (
        <TableSkeleton />
      ) : detailLog ? (
        <div className="data-enter">
          <Table<DetailLog>
            className="detail-log-page__table"
            columns={columns}
            dataSource={pagedDetailLog}
            loading={loadingDetailLog}
            rowKey="id"
            locale={{
              emptyText: (
                <Empty
                  description={
                    hasActiveFilters ? (
                      <span>
                        Sin registros para los filtros actuales.
                        <br />
                        <Button
                          type="link"
                          onClick={handleClearFilters}
                          disabled={isBusy}
                        >
                          Limpiar filtros
                        </Button>
                      </span>
                    ) : (
                      "No hay registros en esta ejecución."
                    )
                  }
                />
              ),
            }}
            pagination={{
              ...pagination,
              ...TABLE_PAGINATION_UX,
              showTotal: (total) =>
                `${total.toLocaleString("es-CO")} ${total === 1 ? "registro" : "registros"}`,
            }}
            onChange={handleTableChange}
            scroll={{ x: isOrderProcess ? 1080 : 980 }}
          />
        </div>
      ) : (
        <Empty description="No se pudo cargar el detalle del log." />
      )}
    </div>
  );
};

export default DetailLogPage;
